import React, { useState, useEffect, useCallback, useRef } from "react";

interface Props {
  onChange: (dataUrl: string | null, text: string) => void;
}

const FONTS = [
  { name: "Dancing Script", label: "Elegant" },
  { name: "Great Vibes", label: "Classic" },
  { name: "Pacifico", label: "Casual" },
  { name: "Caveat", label: "Handwritten" },
];

// Build the Google Fonts URL once
const GOOGLE_FONTS_URL = `https://fonts.googleapis.com/css2?family=${FONTS.map(
  (f) => f.name.replace(/ /g, "+")
).join("&family=")}&display=swap`;

export default function SignatureTyped({ onChange }: Props) {
  const [text, setText] = useState("");
  const [selectedFont, setSelectedFont] = useState(FONTS[0].name);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const linkRef = useRef<HTMLLinkElement | null>(null);

  // ─── Load Google Fonts ────────────────────────────────────────────────────────

  useEffect(() => {
    // Check if already loaded
    const existing = document.querySelector(
      `link[href="${GOOGLE_FONTS_URL}"]`
    );
    if (existing) {
      setFontsLoaded(true);
      return;
    }

    const link = document.createElement("link");
    link.href = GOOGLE_FONTS_URL;
    link.rel = "stylesheet";
    document.head.appendChild(link);
    linkRef.current = link;

    // Wait for fonts to be ready
    document.fonts.ready.then(() => {
      setFontsLoaded(true);
    });

    return () => {
      // Don't remove — other instances might need it
    };
  }, []);

  // ─── Render to canvas for data URL export ─────────────────────────────────────

  const renderToCanvas = useCallback(() => {
    if (!text.trim()) {
      onChange(null, "");
      return;
    }

    // Wait for fonts before rendering
    const doRender = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 500;
      canvas.height = 150;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // White background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, 500, 150);

      // Text
      ctx.font = `36px "${selectedFont}", cursive`;
      ctx.fillStyle = "#1a1a3e";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Truncate if too long
      const maxWidth = 460;
      let fontSize = 36;
      ctx.font = `${fontSize}px "${selectedFont}", cursive`;
      while (ctx.measureText(text).width > maxWidth && fontSize > 16) {
        fontSize -= 2;
        ctx.font = `${fontSize}px "${selectedFont}", cursive`;
      }

      ctx.fillText(text, 250, 75, maxWidth);

      onChange(canvas.toDataURL("image/png"), text);
    };

    if (fontsLoaded) {
      document.fonts.ready.then(doRender);
    } else {
      // Fallback: try after short delay
      setTimeout(doRender, 500);
    }
  }, [text, selectedFont, fontsLoaded, onChange]);

  useEffect(() => {
    renderToCanvas();
  }, [renderToCanvas]);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="w-full">
      {/* Text Input */}
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type your full name"
        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 mb-4"
        autoFocus
      />

      {/* Font Selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FONTS.map((font) => (
          <button
            key={font.name}
            onClick={() => setSelectedFont(font.name)}
            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
              selectedFont === font.name
                ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            <span
              style={{ fontFamily: `"${font.name}", cursive` }}
            >
              {font.label}
            </span>
          </button>
        ))}
      </div>

      {/* Preview */}
      <div className="border border-slate-200 rounded-xl bg-white p-6 min-h-[120px] flex items-center justify-center">
        {text.trim() ? (
          <span
            style={{
              fontFamily: `"${selectedFont}", cursive`,
              fontSize: "36px",
              color: "#1a1a3e",
            }}
            className="break-all text-center max-w-full"
          >
            {text}
          </span>
        ) : (
          <span className="text-slate-300 text-sm">
            Type your name above
          </span>
        )}
      </div>
    </div>
  );
}
